// Bindable actions and the input codes bound to them.
// Input codes are KeyboardEvent.code values ("KeyW", "Space", "ControlLeft"...), "Mouse0".."Mouse4",
// "WheelUp" and "WheelDown".

export type Action =
  | 'forward'
  | 'back'
  | 'moveleft'
  | 'moveright'
  | 'jump'
  | 'duck'
  | 'attack1'
  | 'attack2'
  | 'use'
  | 'inspect'
  | 'randomFinish'
  | 'restart'
  | 'spectate'
  | 'chat'
  | 'zone'
  | 'switchWeapon'
  | 'drop'
  | 'reload'
  | 'debug'
  | 'noclip'
  | 'scores'
  | 'mute';

export interface ActionInfo {
  id: Action;
  label: string;
  /** Held actions stay on while the input is down; the rest fire once per press. */
  held: boolean;
}

export const ACTIONS: readonly ActionInfo[] = [
  { id: 'forward', label: 'Forward', held: true },
  { id: 'back', label: 'Back', held: true },
  { id: 'moveleft', label: 'Strafe left', held: true },
  { id: 'moveright', label: 'Strafe right', held: true },
  { id: 'jump', label: 'Jump', held: true },
  { id: 'duck', label: 'Crouch', held: true },
  { id: 'attack1', label: 'Slash / shoot', held: true },
  { id: 'attack2', label: 'Stab / silencer', held: true },
  { id: 'use', label: 'Use (buttons)', held: true },
  { id: 'inspect', label: 'Inspect knife', held: false },
  { id: 'randomFinish', label: 'Change skin (random knife finish)', held: false },
  { id: 'switchWeapon', label: 'Knife / pistol', held: false },
  { id: 'drop', label: 'Drop weapon (!knife, !usp to get it back)', held: false },
  { id: 'reload', label: 'Reload', held: true },
  { id: 'restart', label: 'Restart run', held: false },
  { id: 'spectate', label: 'Spectate the record (WR)', held: false },
  { id: 'chat', label: 'Chat and commands (!help)', held: false },
  { id: 'scores', label: 'Hiscores (hold)', held: true },
  { id: 'mute', label: 'Mute music', held: false },
  { id: 'zone', label: 'Zone corner', held: false },
  { id: 'debug', label: 'Debug overlay', held: false },
  { id: 'noclip', label: 'Noclip (testing)', held: false },
];

export const MAX_BINDS_PER_ACTION = 3;

export type Keybinds = Record<Action, string[]>;

export function defaultKeybinds(): Keybinds {
  return {
    forward: ['KeyW'],
    back: ['KeyS'],
    moveleft: ['KeyA'],
    moveright: ['KeyD'],
    jump: ['Space', 'WheelUp', 'WheelDown'],
    duck: ['ControlLeft', 'KeyC', 'ControlRight'],
    attack1: ['Mouse0'],
    attack2: ['Mouse2'],
    use: ['KeyE'],
    inspect: ['KeyF'],
    randomFinish: ['KeyP'],
    restart: ['KeyR'],
    spectate: ['KeyL'],
    chat: ['KeyY'],
    zone: ['KeyX'],
    switchWeapon: ['KeyQ'],
    drop: ['KeyG'],
    reload: [],
    debug: ['F3'],
    noclip: ['KeyZ'],
    scores: ['Tab'],
    mute: ['KeyM'],
  };
}

/** Fill in missing actions from defaults and drop junk, so old or hand-edited settings still work. */
export function sanitizeKeybinds(raw: unknown): Keybinds {
  const defaults = defaultKeybinds();
  if (!raw || typeof raw !== 'object') return defaults;
  const out = defaultKeybinds();
  const seen = new Set<string>();
  const missing: Action[] = [];
  const take = (codes: unknown[]) => {
    const list: string[] = [];
    for (const c of codes) {
      if (typeof c !== 'string' || !c || seen.has(c) || list.length >= MAX_BINDS_PER_ACTION) continue;
      seen.add(c);
      list.push(c);
    }
    return list;
  };
  // Noclip took over Z from "zone corner" (now X): move it in settings saved before that.
  const saved = raw as Record<string, unknown>;
  if (!('noclip' in saved) && Array.isArray(saved.zone) && saved.zone.includes('KeyZ'))
    raw = { ...saved, zone: saved.zone.map((c) => (c === 'KeyZ' ? 'KeyX' : c)) };
  for (const a of ACTIONS) {
    const v = (raw as Record<string, unknown>)[a.id];
    if (Array.isArray(v)) out[a.id] = take(v);
    else missing.push(a.id);
  }
  // Actions missing from saved settings get their defaults, minus keys already used elsewhere.
  for (const a of missing) out[a] = take(defaults[a]);
  return out;
}

/** Bind `code` to `action` (removing it from any other action). Replaces slot `slot`, or appends. */
export function bind(binds: Keybinds, action: Action, code: string, slot?: number) {
  for (const a of ACTIONS) binds[a.id] = binds[a.id].filter((c) => c !== code);
  const list = binds[action];
  if (slot !== undefined && slot < list.length) list[slot] = code;
  else if (list.length < MAX_BINDS_PER_ACTION) list.push(code);
  else list[list.length - 1] = code;
}

export function unbind(binds: Keybinds, action: Action, code: string) {
  binds[action] = binds[action].filter((c) => c !== code);
}

/** Reverse lookup: input code -> action. */
export function buildLookup(binds: Keybinds): Map<string, Action> {
  const m = new Map<string, Action>();
  for (const a of ACTIONS) for (const c of binds[a.id]) m.set(c, a.id);
  return m;
}

const NAMES: Record<string, string> = {
  Space: 'Space',
  ControlLeft: 'L Ctrl',
  ControlRight: 'R Ctrl',
  ShiftLeft: 'L Shift',
  ShiftRight: 'R Shift',
  AltLeft: 'L Alt',
  AltRight: 'R Alt',
  MetaLeft: 'L Meta',
  MetaRight: 'R Meta',
  Mouse0: 'Mouse 1',
  Mouse1: 'Mouse 3',
  Mouse2: 'Mouse 2',
  Mouse3: 'Mouse 4',
  Mouse4: 'Mouse 5',
  WheelUp: 'Wheel up',
  WheelDown: 'Wheel down',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  CapsLock: 'Caps',
  Escape: 'Esc',
  Enter: 'Enter',
  Tab: 'Tab',
  Backspace: 'Backspace',
};

/** Human-readable name for an input code. Mouse numbering follows CS (Mouse 1 = left, Mouse 2 = right). */
export function inputName(code: string): string {
  if (NAMES[code]) return NAMES[code];
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return 'Num ' + code.slice(6);
  return code;
}
